import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton, GhostButton } from "@/components/kit";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshCompany } = useAuth();
  const isCancel = location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "polling");

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refreshCompany?.(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (tries >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, location.search]); // eslint-disable-line

  const CONTENT = {
    polling: { icon: <Loader2 className="h-12 w-12 text-orange-500 animate-spin" />, title: "Confirming your payment…", sub: "This only takes a moment." },
    paid: { icon: <CheckCircle2 className="h-12 w-12 text-emerald-400" />, title: "Subscription active!", sub: "Your plan has been upgraded. Thank you." },
    pending: { icon: <Loader2 className="h-12 w-12 text-amber-400" />, title: "Payment processing", sub: "We'll update your plan as soon as it's confirmed." },
    failed: { icon: <XCircle className="h-12 w-12 text-rose-400" />, title: "Payment not completed", sub: "No charge was made. You can try again." },
    cancelled: { icon: <XCircle className="h-12 w-12 text-slate-400" />, title: "Checkout cancelled", sub: "No charge was made." },
    error: { icon: <XCircle className="h-12 w-12 text-rose-400" />, title: "Something went wrong", sub: "We couldn't find your checkout session." },
  };
  const c = CONTENT[state] || CONTENT.error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="payment-result-page">
      <div className="w-full max-w-md bg-slate-900/90 border border-slate-700 rounded-lg p-8 text-center shadow-2xl">
        <div className="flex justify-center mb-4">{c.icon}</div>
        <h1 className="font-heading text-2xl font-extrabold uppercase text-slate-100" data-testid="payment-result-title">{c.title}</h1>
        <p className="text-sm text-slate-400 mt-2">{c.sub}</p>
        <div className="mt-6 flex gap-2 justify-center">
          <PrimaryButton onClick={() => navigate("/billing")} data-testid="payment-result-billing">Go to billing</PrimaryButton>
          <GhostButton onClick={() => navigate("/")}>Dashboard</GhostButton>
        </div>
      </div>
    </div>
  );
}
